import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";

export const TieAccountsDialog = ({ side, data, open, onClose, onSaved, testPrefix }) => {
  const rows = (data?.rows || []).filter((r) => r.gl_account);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setSel(new Set(data?.scope || rows.map((r) => r.gl_account)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (gl, v) => setSel((s) => { const n = new Set(s); v ? n.add(gl) : n.delete(gl); return n; });
  const save = async (accounts) => {
    setBusy(true);
    try {
      await axios.put(`${API}/reports/${side}-ageing/tb-tie-scope`, { accounts });
      toast.success(accounts ? `Tie now uses ${accounts.length} account(s) for everyone` : "Tie accounts reset to default");
      onSaved(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the tie accounts");
    } finally { setBusy(false); }
  };
  const meta = data?.scope_meta;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`${testPrefix}-tie-accounts-dialog`}>
        <DialogHeader><DialogTitle>Accounts counted in the Trial Balance tie</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Applies to everyone. {meta?.by ? `Last changed by ${meta.by} on ${new Date(meta.at).toLocaleString()}.` : "Currently the default list."}</p>
        <div className="max-h-[55vh] overflow-auto divide-y divide-slate-100 rounded-md border border-slate-200">
          {rows.map((r) => (
            <label key={r.gl_account} className="flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-slate-50" data-testid={`${testPrefix}-tie-account-${r.gl_account}`}>
              <Checkbox checked={sel.has(r.gl_account)} onCheckedChange={(v) => toggle(r.gl_account, !!v)} data-testid={`${testPrefix}-tie-account-check-${r.gl_account}`} />
              <span className="font-mono text-slate-500 w-20">{r.gl_account}</span>
              <span className="flex-1 font-medium text-slate-800">{r.name}</span>
              <span className="tabular-nums text-slate-600">{inr(r.tb_balance)}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-slate-500" data-testid={`${testPrefix}-tie-accounts-count`}>{sel.size} of {rows.length} selected</p>
        <DialogFooter className="gap-2">
          {data?.scope && <Button variant="outline" disabled={busy} onClick={() => save(null)} data-testid={`${testPrefix}-tie-accounts-reset`}>{data?.scope_default ? "Reset to default" : "Reset to all accounts"}</Button>}
          <Button variant="outline" onClick={onClose} data-testid={`${testPrefix}-tie-accounts-cancel`}>Cancel</Button>
          <Button disabled={busy || sel.size === 0} onClick={() => save([...sel])} className="bg-slate-900 text-white hover:bg-slate-800" data-testid={`${testPrefix}-tie-accounts-save`}>Save for everyone</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
